import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComingSoonCta from '@/components/feature/ComingSoonCta';

const tracks = [
  { number: '01', title: 'El primer latido', desc: 'el pulso que tu bebé conoció primero' },
  { number: '02', title: 'Las aguas del comienzo', desc: 'el fluido que lo envolvía, en sonido' },
  { number: '03', title: 'El corazón que escuchaba', desc: 'el grave más profundo del álbum' },
  { number: '04', title: 'Nana sin palabras', desc: 'una melodía suave sin letra' },
  { number: '05', title: 'El eco del vientre', desc: 'el eco constante del hogar interior' },
  { number: '06', title: 'Respiración del alba', desc: 'un ritmo que sube y baja como el aliento' },
  { number: '07', title: 'El primer abrazo', desc: 'calidez envolvente, como un abrazo sostenido' },
  { number: '08', title: 'Quietud profunda', desc: 'el tono más grave de todo el álbum' },
  { number: '09', title: 'La voz que espera', desc: 'un fondo cálido y constante' },
  { number: '10', title: 'Volver al origen', desc: 'el cierre, un eco del primer tema' },
];

const heroImage = 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20real%20mother%20gently%20holding%20her%20newborn%20baby%20close%20to%20her%20chest%20in%20a%20dark%20warm%20nursery%20with%20womb-like%20ambient%20lighting%2C%20soft%20amber%20and%20deep%20terracotta%20shadows%2C%20mother%20and%20baby%20in%20intimate%20nighttime%20connection%2C%20the%20atmosphere%20feels%20like%20being%20inside%20a%20warm%20safe%20space%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20peaceful%20and%20safe%20atmosphere%2C%20shallow%20depth%20of%20field%2C%20abstract%20soft%20bokeh%20lights%20suggesting%20inner%20warmth&width=1400&height=600&seq=270&orientation=landscape';

export default function AlbumNanasDelUtero() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: '0%' }}
          id="reading-progress"
        />
      </div>

      {/* Hero Section */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-20 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start mb-10 md:mb-16">
            <div className="w-full md:w-1/2">
              <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
                {t('album_nanas.tag')}
              </span>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-[#8B6F5C] leading-tight">
                {t('album_nanas.title1')}
                <br />
                <span className="font-bold">{t('album_nanas.title2')}</span>
              </h1>
            </div>
            <div className="w-full md:w-1/2 flex flex-col justify-end md:pt-12">
              <p className="font-sans text-base md:text-lg text-[#3D2B1F] leading-relaxed mb-6">
                {t('album_nanas.hero.desc')}
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-user-smile-line text-xs" />
                  {t('album_nanas.badge1')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-music-line text-xs" />
                  10 temas
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-download-cloud-line text-xs" />
                  {t('album.descarga')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-infinity-line text-xs" />
                  {t('album.uso_ilimitado')}
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-serif text-4xl md:text-5xl font-bold text-[#8B6F5C]">$9.99</span>
                <span className="font-sans text-sm text-[#5C4A3A]">{t('common.usd')} — {t('common.pago_unico')}</span>
              </div>
              <ComingSoonCta />
            </div>
          </div>
          <div className="w-full h-[320px] md:h-[420px] rounded-3xl overflow-hidden">
            <img
              src={heroImage}
              alt="Madre con bebé en ambiente cálido y seguro como el útero"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Sinopsis */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-heart-pulse-line text-3xl text-cream" />
          </div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-cream leading-tight mb-8">
            {t('album_nanas.sinopsis.title')}
          </h2>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            No fue una voz. Fue el latido de tu corazón. El fluido que lo rodeaba. El ritmo constante de tu respiración. Su cerebro pasó nueve meses aprendiendo que ese sonido significa hogar, seguridad, amor.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            Este álbum lleva ese recuerdo directo al sistema nervioso de tu bebé. No es música convencional: son frecuencias y sonidos diseñados para activar el sistema nervioso parasimpático y reducir el cortisol.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed">
            Para las noches más difíciles. Para las regresiones más intensas. Para cuando nada más funciona y tu bebé necesita recordar que el mundo puede ser seguro otra vez.
          </p>
        </div>
      </section>

      {/* Neurociencia */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <div className="w-full md:w-1/2">
              <div className="w-14 h-14 rounded-2xl bg-[#AAB7A3]/20 flex items-center justify-center mb-6">
                <i className="ri-brain-line text-2xl text-[#AAB7A3]" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#8B6F5C] leading-tight mb-6">
                {t('album_nanas.ciencia.title')}
              </h2>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
                Los sonidos del útero, incluyendo el <strong>latido cardíaco a 60-80 BPM</strong> y el ruido blanco de baja frecuencia, activan el reflejo de calma en recién nacidos. Estos sonidos son el primer lenguaje que aprendió el cerebro de tu bebé.
              </p>
              <p className="font-sans text-base text-[#3D2B1F] leading-relaxed mb-4">
                Durante nueve meses, el cerebro fetal procesó constantemente estos estímulos como señales de seguridad. El latido materno, el flujo de fluidos amnióticos, la respiración profunda — todo formó un paisaje sonoro que significa: <em>estás a salvo, estás en casa</em>.
              </p>

            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-cream rounded-3xl p-8 md:p-10 border border-sand/30">
                <h3 className="font-serif text-2xl font-bold text-[#8B6F5C] mb-6">
                  {t('album.que_incluye')}
                </h3>
                <ul className="flex flex-col gap-4">
                  {[
                    '10 temas de audio de alta calidad',
                    'Latido cardíaco a frecuencia materna (60-80 BPM)',
                    'Ruido blanco de baja frecuencia',
                    'Sonidos de fluidos amnióticos suaves',
                    'Frecuencias diseñadas para activar el nervio vago',
                    'Reducción de cortisol en bebés y mamás',
                    'Descarga inmediata tras la compra',
                    'Uso ilimitado en todos tus dispositivos',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#AAB7A3]/20 flex-shrink-0">
                        <i className="ri-check-line text-[#AAB7A3] text-sm" />
                      </div>
                      <span className="font-sans text-sm text-[#3D2B1F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Track List */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('album.lista_temas')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('album_nanas.tracks_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              Cada tema está diseñado para recrear una faceta del ambiente uterino. Juntos, forman el paisaje sonoro más reconfortante que tu bebé puede escuchar.
            </p>
          </div>

          <div className="space-y-3">
            {tracks.map((track) => (
              <div
                key={track.number}
                className="flex items-baseline gap-3 font-sans text-base md:text-lg text-[#3D2B1F]"
              >
                <span className="font-medium text-[#8B6F5C] w-7 flex-shrink-0">
                  {track.number}
                </span>
                <span className="flex-1">
                  {track.title} — {track.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-moon-clear-line text-3xl text-cream" />
          </div>
          <p className="font-serif text-2xl md:text-3xl text-cream leading-relaxed mb-8">
            &ldquo;Sé lo que es estar ahí, a las 3 de la mañana, con los brazos cansados y el corazón más cansado todavía, buscando algo — lo que sea — que funcione. No hay ningún sonido que reemplace tu presencia, tu voz, tus brazos. Eso ya lo sabés, y está bien que así sea.
            Pero hay noches en que tu cuerpo necesita un aliado. Un sonido que le hable al cerebro de tu bebé en el único idioma que conoce desde antes de nacer, mientras vos, por fin, podés respirar un poco vos también.
            Este álbum no es magia. Es el sonido más antiguo que existe para tu bebé — el que su cerebro reconoció como hogar mucho antes de conocer el mundo de afuera. Dejalo hacer su parte, esta noche.&rdquo;
          </p>
          <div className="w-16 h-px bg-cream/30 mx-auto mb-8" />
          <p className="font-sans text-base text-cream/80 leading-relaxed">
            — Mercedes, NeuroBabys
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
            {t('album_nanas.cta.title')}
          </h2>
          <p className="font-sans text-base text-[#3D2B1F] max-w-xl mx-auto mb-8">
            {t('album_nanas.cta.desc')}
          </p>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="font-serif text-5xl md:text-6xl font-bold text-[#8B6F5C]">$9.99</span>
            <span className="font-sans text-base text-[#5C4A3A]">{t('common.usd')}</span>
          </div>
          <ComingSoonCta size="lg" className="mb-4" />
          <p className="font-sans text-sm text-[#5C4A3A]">
            {t('album.descarga_footer')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}